import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../stores/auth'

const SESSION_WARNING_TIME = 2 * 60 * 1000 // Show warning 2 minutes before timeout
const SESSION_TIMEOUT = 15 * 60 * 1000 // 15 minutes

export function useSessionWarning() {
  const authStore = useAuthStore()
  const showWarning = ref(false)
  const warningTimeoutId = ref<number | null>(null)
  const countdownInterval = ref<number | null>(null)
  const timeRemaining = ref(0)

  const timeRemainingFormatted = computed(() => {
    const minutes = Math.floor(timeRemaining.value / 60)
    const seconds = timeRemaining.value % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  })

  const setupWarningTimeout = () => {
    // Clear existing timeouts
    clearWarningTimeout()

    if (!authStore.isAuthenticated || !authStore.lastActivity) return

    const timeToWarning = SESSION_TIMEOUT - SESSION_WARNING_TIME

    warningTimeoutId.value = window.setTimeout(() => {
      if (authStore.isAuthenticated && !authStore.isSessionExpired) {
        showSessionWarning()
      }
    }, timeToWarning)
  }

  const handleSessionActivity = () => {
    // Don't reset warning timeout if warning is already showing
    if (showWarning.value) return

    if (authStore.isAuthenticated) {
      setupWarningTimeout()
    }
  }

  const showSessionWarning = () => {
    if (!authStore.lastActivity) return

    showWarning.value = true
    timeRemaining.value = Math.floor(SESSION_WARNING_TIME / 1000) // 2 minutes in seconds

    // Start countdown
    countdownInterval.value = window.setInterval(() => {
      timeRemaining.value--

      if (timeRemaining.value <= 0) {
        hideWarning()
        authStore.logout()
      }
    }, 1000)
  }

  const extendSession = () => {
    authStore.updateActivity()
    hideWarning()
    setupWarningTimeout()
  }

  const hideWarning = () => {
    showWarning.value = false
    clearWarningTimeout()
  }

  const clearWarningTimeout = () => {
    if (warningTimeoutId.value) {
      clearTimeout(warningTimeoutId.value)
      warningTimeoutId.value = null
    }
    if (countdownInterval.value) {
      clearInterval(countdownInterval.value)
      countdownInterval.value = null
    }
  }

  const logout = () => {
    authStore.logout()
    hideWarning()
  }

  onMounted(() => {
    // Listen for session activity events
    window.addEventListener('sessionActivity', handleSessionActivity)

    // Setup initial warning timeout if authenticated
    if (authStore.isAuthenticated) {
      setupWarningTimeout()
    }
  })

  onUnmounted(() => {
    window.removeEventListener('sessionActivity', handleSessionActivity)
    clearWarningTimeout()
  })

  return {
    showWarning,
    timeRemainingFormatted,
    extendSession,
    logout,
    hideWarning,
    setupWarningTimeout,
  }
}
